import React from 'react';

import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';

import type { ClaimCampaignStatus } from '~/services/LuxLoyalty';

import { titleCase } from '~/utils/stringUtils';

import { formatMatchCheckUntil } from './matchCheckUntil';

interface HideChallengeConfirmDialogProps {
  /** Whether the dialog is visible */
  open: boolean;
  /** The status the admin is about to save, EXPIRED or PAUSED */
  newStatus: ClaimCampaignStatus;
  /** The challenge's matchCheckUntil (ISO): the cron keeps upgrading members until then */
  matchCheckUntil: string;
  /** Keeps the campaign dialog open with nothing saved */
  onCancel: () => void;
  /** Saves the campaign with the new status */
  onConfirm: () => void;
}

/**
 * Shown before an active Status Challenge is saved as EXPIRED or PAUSED while it's still checking members.
 * The upgrade still happens, but the Customer Portal hides the challenge unless the campaign is ACTIVE, so
 * members lose sight of their progress. The caller decides when to show it (shouldWarnStatusHidesChallenge).
 */
export default function HideChallengeConfirmDialog({
  open,
  newStatus,
  matchCheckUntil,
  onCancel,
  onConfirm,
}: HideChallengeConfirmDialogProps) {
  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Hide this challenge from customers?</DialogTitle>
      <DialogContent>
        <DialogContentText component="div">
          <Alert severity="warning">
            Members can still be upgraded until <strong>{formatMatchCheckUntil(matchCheckUntil)}</strong>, but while the
            campaign is {titleCase(newStatus)} they won&apos;t see their challenge or progress in the Customer Portal.
          </Alert>
          <Typography variant="body2" component="p" color="text.secondary" sx={{ mt: 2 }}>
            To stop new enrolments, leave the status Active: enrolment already closes at the end date.
          </Typography>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onCancel} autoFocus>
          Cancel
        </Button>
        <Button variant="contained" color="primary" onClick={onConfirm}>
          Change status
        </Button>
      </DialogActions>
    </Dialog>
  );
}
